/*
 * Copyright (c) Kalaora 2022.
 * Author: fharold
 *
 * hilo-oauth-manager
 * HomePage.tsx
 */

import {PolicyListener} from "../../services/PolicyService";
import Policy from "../../tools/provider/Policy";
import HiloReactComponent from "../../tools/view/layout/HiloReactComponent";
import React from "react";
import PageTitle, {TitleTab} from "../../tools/view/layout/PageTitle";
import Profile from "../../models/Profile";
import ProfileAdapter, {ProfileAdapterListener} from "../adapter/ProfileAdapter";
import NavigationService from "../../tools/service/NavigationService";
import List from "../../tools/list/List";
import Spinner from "../../tools/view/element/Spinner";
import PageComponent from "../../tools/view/layout/PageComponent";
import ProfileService from "../../services/ProfileService";
import ProfileDataProvider from "../../provider/ProfileDataProvider";
import FormModal from "../../tools/view/layout/FormModal";
import ReactDOM from "react-dom";
import CatalogService from "../../services/CatalogService";
import UIToast from "../../tools/view/toast/UIToast";
import AddProfileForm from "../../models/form/profile/AddProfileForm";
import UserService from "../../services/UserService";
import {ProfileDTO} from "../../models/dto/profile/ProfileDTO";
import {SelectField} from "../../tools/model/form/SelectField";
import {TextField} from "../../tools/model/form/TextField";

interface State {
    policy: Policy;
    pageIndex: number;
    profileDataProvider: ProfileDataProvider;
    profileAdapter?: ProfileAdapter;
    profileForm: AddProfileForm;
}

export default class ProfilesPage extends HiloReactComponent<{}, State> implements PolicyListener, ProfileAdapterListener {
    private readonly _catalogService: CatalogService;
    private readonly _navigationService: NavigationService;
    private readonly _profileService: ProfileService;
    private readonly _userService: UserService;
    private _modalContainer?: Element;

    /**
     * constructor
     * @param props
     * @param state
     */
    constructor(props: {}, state: State) {
        super(props, state);

        this._navigationService = this._serviceRepository.resolve(NavigationService.id) as NavigationService;
        this._profileService = this._serviceRepository.resolve(ProfileService.id) as ProfileService;
        this._catalogService = this._serviceRepository.resolve(CatalogService.id) as CatalogService;
        this._userService = this._serviceRepository.resolve(UserService.id) as UserService;

        const profileDataProvider = new ProfileDataProvider(this._profileService);

        this.state = {
            policy: this._policyService.policy,
            pageIndex: 0,
            profileDataProvider: profileDataProvider,
            profileAdapter: new ProfileAdapter(this.t, profileDataProvider),
            profileForm: new AddProfileForm([], [])
        };
    }

    /**
     * rowSelected
     * @param obj
     */
    public rowSelected(obj: Profile): void {
        this._navigationService.load(`/profile/${obj.id}`)
    }

    /**
     * componentDidMount
     */
    public componentDidMount(): void {
        super.componentDidMount();
        this._profileService.addListener(this);
        this._modalContainer = document.querySelector('#modal-container') || undefined;
        this._policyService.addListener(this);

        if (this.state.profileAdapter) {
            this.state.profileAdapter.listener = this;
        }
        this._catalogService.findAll().then(catalogs => {
            this.state.profileForm.loadCatalogs(catalogs);
        })
    }

    /**
     * componentWillUnmount
     */
    public componentWillUnmount(): void {
        this._policyService.removeListener(this);
        this._profileService.removeListener(this);

        // this.state.profileDataProvider.unsubscribe(this);

        super.componentWillUnmount();
    }

    /**
     * policyUpdated
     * @param policy
     */
    public policyUpdated = (policy: Policy) => {
        this.setState({
            policy: policy
        })
    };

    /**
     * _saveProfile
     * @param dto
     * @private
     */
    private _saveProfile = async (dto: Map<string, any> | FormData) => {
        try {
            let data = this.state.profileForm.compile();
            await this._profileService.createProfile(data);
            this.state.profileForm.statusCode = 200;
            UIToast.default.show(this.t.profileCreated)
            window.location.reload();
        } catch (e) {
            this.state.profileForm.statusCode = e.status
            console.error(e);
        }
    };

    /**
     * _displayAddUserModal
     * @private
     */
    private _displayAddUserModal = async () => {
        if (!this._modalContainer || !this.state.profileForm) {
            return;
        }

        this.state.profileForm._clearForm();

        const modal = <FormModal
            emptyForm={this.state.profileForm}
            title={t => t.newProfile}
            onSaveClicked={this._saveProfile}
        />;

        ReactDOM.render(modal, this._modalContainer);
    }

    /**
     * _profileList
     * @returns {any}
     * @private
     */
    private _profileList = (props: { provider?: ProfileDataProvider, adapter?: ProfileAdapter }) => {
        const {provider, adapter} = props;

        if (!adapter || !provider) return <Spinner/>;

        return <List
            onAddClick={() => this._displayAddUserModal()}
            adapter={adapter}
            dataProvider={provider}/>
    }

    /**
     * render
     */
    public render(): any {
        const {profileDataProvider, profileAdapter} = this.state;

        const titleTabs: TitleTab[] = [
            {title: t => t.list, content: <this._profileList provider={profileDataProvider} adapter={profileAdapter}/>}
        ];

        return <PageComponent>
            <PageTitle title={t => t.profiles} tabs={titleTabs}
                       onTabSelected={index => this.setState({pageIndex: index})}/>
            {
                titleTabs[this.state.pageIndex].content
            }
        </PageComponent>
    }
}